import type { SessionRef } from '../types.ts';

// Left slide-over drawer showing past chat sessions, grouped by recent vs.
// archived. Click a session restores its conversation state.
export function HistoryDrawer({
  open,
  sessions,
  onClose,
  onSelect,
}: {
  open: boolean;
  sessions: SessionRef[];
  onClose: () => void;
  onSelect?: (id: string) => void;
}) {
  if (!open) return null;
  const active = sessions.filter((s) => !s.archived);
  const archived = sessions.filter((s) => s.archived);
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label="close history drawer"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 30,
          cursor: 'default',
        }}
      />
      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: 320,
          maxWidth: '85vw',
          background: 'var(--bg-1)',
          borderRight: '1px solid var(--line-2)',
          zIndex: 31,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            borderBottom: '1px solid var(--line-1)',
          }}
        >
          <span
            className="caps mono"
            style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
          >
            history
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ color: 'var(--fg-3)', fontSize: 16 }}
            aria-label="close"
          >
            ×
          </button>
        </div>
        <SectionList label="recent" items={active} onSelect={onSelect} />
        {archived.length > 0 && (
          <SectionList label="archived" items={archived} onSelect={onSelect} />
        )}
      </aside>
    </>
  );
}

function SectionList({
  label,
  items,
  onSelect,
}: {
  label: string;
  items: SessionRef[];
  onSelect?: (id: string) => void;
}) {
  return (
    <div style={{ padding: '8px 0' }}>
      <div
        className="caps mono"
        style={{
          color: 'var(--fg-4)',
          fontSize: 9,
          letterSpacing: '0.1em',
          padding: '6px 14px',
        }}
      >
        {label}
      </div>
      {items.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect?.(s.id)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '8px 14px',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <span style={{ color: 'var(--fg-1)', fontSize: 13 }}>
            {s.pinned && <span style={{ color: 'var(--accent-2)', marginRight: 4 }}>★</span>}
            {s.title}
          </span>
          <span
            className="mono"
            style={{ color: 'var(--fg-3)', fontSize: 10, display: 'flex', gap: 8 }}
          >
            <span>{s.relative}</span>
            <span>·</span>
            <span>{s.regulation}</span>
            <span>·</span>
            <span>{s.turns} turns</span>
          </span>
        </button>
      ))}
    </div>
  );
}
